import { useEffect, useMemo, useState } from 'react';
import { getMyAgencyStudents } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { GraduationCap, Search, Users, X } from 'lucide-react';
import {
  EmptyState,
  KpiCard,
  StatusBadge,
  formatDate,
  fullName,
  safeList,
} from './AgencyPortalShared';

export default function AgencyStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getMyAgencyStudents();
        if (!cancelled) setStudents(res.data || []);
      } catch (err) {
        console.error(err);
        if (!cancelled) setStudents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return safeList(students).filter((student) =>
      [
        fullName(student),
        student.email,
        student.phone,
        student.program_name,
        student.enrollment_status,
        student.campus,
        student.delivery_method,
      ].filter(Boolean).join(' ').toLowerCase().includes(q),
    );
  }, [students, query]);
  const hasSearch = Boolean(query.trim());

  const activeCount = safeList(students).filter((student) =>
    ['active', 'admitted', 'waiting payment', 'waiting sp'].includes(String(student.enrollment_status || '').toLowerCase()),
  ).length;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          Students
        </h2>
        <p className="mt-1 text-sm text-[#062B1A]/60">Students linked to your agency.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <KpiCard label="Linked Students" value={students.length} icon={Users} />
        <KpiCard label="Active Records" value={activeCount} icon={GraduationCap} accent />
        <KpiCard label="Programs" value={new Set(safeList(students).map((student) => student.program_name).filter(Boolean)).size} icon={GraduationCap} />
      </div>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardContent className="p-4">
          <div className="relative w-full lg:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#062B1A]/35" />
            <Input
              type="search"
              aria-label="Search students"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, email, phone, or program..."
              className="h-10 border-[rgba(6,43,26,0.16)] pl-9 pr-9 focus-visible:border-[#D4AF37] focus-visible:ring-[3px] focus-visible:ring-[rgba(212,175,55,0.35)] [&::-webkit-search-cancel-button]:appearance-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear student search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[#062B1A]/40 hover:bg-[#062B1A]/5 hover:text-[#062B1A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Linked Student List
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-16 animate-pulse rounded-lg bg-[#F7F8FA]" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState icon={Users} title={hasSearch ? 'No students match your search' : 'No students found'}>
              {hasSearch ? `No linked students match “${query.trim()}”. Clear the search to see everyone.` : undefined}
            </EmptyState>
          ) : (
            <div className="overflow-hidden rounded-lg border border-[rgba(6,43,26,0.08)]">
              <div className="hidden grid-cols-[1.1fr_1.3fr_0.9fr_0.8fr_0.8fr] gap-4 bg-[#F7F8FA] px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45 lg:grid">
                <span>Student</span>
                <span>Program</span>
                <span>Campus</span>
                <span>Status</span>
                <span>Start Date</span>
              </div>
              <div className="divide-y divide-[rgba(6,43,26,0.06)]">
                {filtered.map((student) => (
                  <div key={student.id} className="grid gap-3 px-4 py-4 lg:grid-cols-[1.1fr_1.3fr_0.9fr_0.8fr_0.8fr] lg:items-center lg:gap-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-[#062B1A]">{fullName(student)}</p>
                      <p className="truncate text-xs text-[#062B1A]/50">{student.email || 'No email'}</p>
                    </div>
                    <p className="text-sm text-[#062B1A]">{student.program_name || 'Program not set'}</p>
                    <p className="text-sm text-[#062B1A]/65">{student.campus || 'Not set'}</p>
                    <div>
                      <StatusBadge status={student.enrollment_status || 'linked'} />
                    </div>
                    <p className="text-sm text-[#062B1A]/65">{formatDate(student.start_date)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
